/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { Modal } from '../../../shared/modals/Modal';
import { Pressable, StyleSheet, Text, TouchableOpacity, useWindowDimensions, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { settingsStyles as styles } from '../../../settings/screens/settingsScreen.styles';
import { spacing } from '../../../shared/theme/spacing';
import { ticketColors } from '../../../shared/theme/ticketColors';
import { InventoryIsoDateField } from '../../../shared/ui/InventoryIsoDateField';
import { formatIsoDate, parseIsoDate } from '@shared/vueltoDateTime';
import { useModalBackdropPadding } from '../../../shared/ui/hooks-ui/useModalBackdropPadding';
import { ModalFormField } from '../../../shared/modals/ModalFormField'
import { ModalScreenHeader } from '../../../shared/modals/ModalScreenHeader'
import { modalExperience } from '../../../shared/modals/modalExperience.styles';
import { modalShellShared } from '../../../shared/modals/modalShellShared.styles';
import { posSharedUi as posUi } from '../../../shared/ui/posSharedUi';

type Props = {
  visible: boolean;
  onClose: () => void;
  customRangeFrom: Date;
  customRangeTo: Date;
  setCustomRangeFrom: (d: Date) => void;
  setCustomRangeTo: (d: Date) => void;
};

export function CashCustomRangeModal({
  visible,
  onClose,
  customRangeFrom,
  customRangeTo,
  setCustomRangeFrom,
  setCustomRangeTo,
}: Props) {
  const modalBackdropPadding = useModalBackdropPadding();
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const insets = useSafeAreaInsets();

  const fromIso = formatIsoDate(customRangeFrom);
  const toIso = formatIsoDate(customRangeTo);

  const applyFrom = (iso: string) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(iso.trim())) return;
    setCustomRangeFrom(parseIsoDate(iso));
  };
  const applyTo = (iso: string) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(iso.trim())) return;
    setCustomRangeTo(parseIsoDate(iso));
  };

  return (
    <Modal visible={visible} animationType={isMobile ? 'slide' : 'fade'} transparent onRequestClose={onClose} accessibilityViewIsModal>
      <View
        style={[
          posUi.modalBackdrop,
          isMobile ? { justifyContent: 'flex-end' } : modalExperience.modalRoot,
          isMobile ? null : modalBackdropPadding,
        ]}
      >
        <Pressable
          style={StyleSheet.absoluteFill}
          onPress={onClose}
          accessibilityRole="button"
          accessibilityLabel="Cerrar"
        />
        <View
          style={[
            isMobile
              ? [sheetStyles.sheetCard, { paddingBottom: Math.max(insets.bottom, spacing.sm) }]
              : [
                  posUi.modalCard,
                  modalShellShared.modalCardShell,
                  modalExperience.modalWidthStandard,
                  { maxHeight: '92%', gap: spacing.sm },
                ],
          ]}
        >
          {isMobile ? <View style={sheetStyles.grabber} /> : null}
          <ModalScreenHeader title="Rango personalizado" subtitle="Filtrar historial de cierres" onClose={onClose} />
          <View style={{ flexShrink: 1, minHeight: 0 }}>
            <View style={modalExperience.formPanel}>
              <ModalFormField label="Desde">
                <InventoryIsoDateField
                  value={fromIso}
                  onChange={applyFrom}
                  emptyLabel="Elegí desde"
                  ariaLabel="Fecha desde"
                  showClearButton={false}
                  iosEmptyBehavior="openPicker"
                />
              </ModalFormField>
              <ModalFormField label="Hasta">
                <InventoryIsoDateField
                  value={toIso}
                  onChange={applyTo}
                  emptyLabel="Elegí hasta"
                  ariaLabel="Fecha hasta"
                  showClearButton={false}
                  iosEmptyBehavior="openPicker"
                />
              </ModalFormField>
            </View>
          </View>
          {customRangeFrom.getTime() > customRangeTo.getTime() ? (
            <Text style={[styles.caption, { color: ticketColors.bad, marginBottom: spacing.xs }]}>
              La fecha Desde debe ser anterior a Hasta.
            </Text>
          ) : null}
          <TouchableOpacity
            style={[styles.secondaryButton, customRangeFrom.getTime() > customRangeTo.getTime() && styles.disabled]}
            onPress={onClose}
            disabled={customRangeFrom.getTime() > customRangeTo.getTime()}
            accessibilityRole="button"
          >
            <Text style={styles.secondaryButtonText}>Listo</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
}

const sheetStyles = StyleSheet.create({
  sheetCard: {
    width: '100%',
    maxHeight: '92%',
    backgroundColor: ticketColors.paper,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    borderTopWidth: 1,
    borderColor: ticketColors.ruleSoft,
    paddingTop: 8,
    overflow: 'hidden',
    zIndex: 1,
  },
  grabber: {
    width: 36,
    height: 4,
    borderRadius: 2,
    backgroundColor: ticketColors.rule,
    alignSelf: 'center',
    marginBottom: spacing.xs,
  },
});
